import React, { useState } from 'react';
import { Button, Checkbox, DatePicker, Form, Input, Segmented, Space, Tooltip } from 'antd';
import { FilePdfOutlined, SafetyCertificateOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { OrderStepContext } from '../orderStepContext';
import { Callout, StepSection } from '../OrderProcessParts';
import { OrderLinesSummaryTable } from '../OrderProcessTables';
import OfferPdfPreview, { type OfferPdfPreviewStatus } from './OfferPdfPreview';
import {
  OFFER_ACCEPTANCE_NOTES_MAX,
  findLatestStatusEntry,
  type OfferAcceptance,
} from '../orderOfferAcceptance';
import type { OrderDocumentLanguage } from '../../api/ordersApi';
import { formatDateTimeEuropean } from '../../../../utils/dateUtils';

/**
 * Oferta (makieta `sOferta`): the offer is today's order PDF (no separate
 * `OF/…` number — decision 2026-10-04), shown in the card for the roles that
 * may download it, and the customer's acceptance, saved as the note of the
 * CONFIRMED status change by „Zarejestruj potwierdzenie”. The acceptance form
 * is there from the first moment of the step — also while the offer is only
 * prepared (Szkic): the customer may accept an offer sent outside the system,
 * and nothing requires „Oferta wysłana” first.
 *
 * „Wyślij do klienta” is the existing customer e-mail (order status e-mail
 * with the offer as its edited text; no PDF attachment — the mechanism has
 * none). The status „Oferta wysłana” is set after that e-mail went out or by
 * „Oznacz ofertę jako wysłaną” (action bar) — never before anything was sent.
 */
export default function OfferStep({
  ctx,
  acceptance,
  onAcceptanceChange,
  documentLanguage,
  onDocumentLanguageChange,
  onDownloadPdf,
  pdfDownloading,
  onSendOffer,
  sendOfferDisabledReason,
  loadPdfPreview,
}: {
  ctx: OrderStepContext;
  acceptance: OfferAcceptance;
  onAcceptanceChange: React.Dispatch<React.SetStateAction<OfferAcceptance>>;
  documentLanguage: OrderDocumentLanguage;
  onDocumentLanguageChange: (language: OrderDocumentLanguage) => void;
  onDownloadPdf: () => void;
  pdfDownloading: boolean;
  /** Opens the customer e-mail with the offer; absent when the reader cannot send it. */
  onSendOffer?: () => void;
  /** Why the e-mail cannot be sent (no address, e-mails off) — the button stays visible, disabled. */
  sendOfferDisabledReason?: string | null;
  /** Same PDF as the download button; absent when the reader cannot download it (the table of lines stays). */
  loadPdfPreview?: (language: OrderDocumentLanguage) => Promise<Blob>;
}) {
  const { t } = useTranslation(['orders']);
  const { order, readOnly, caps } = ctx;
  const canDownloadPdf = caps.canDownloadOrderPdf ?? caps.canViewSalesDocuments;
  const confirmedEntry = findLatestStatusEntry(order.statusHistory, 'CONFIRMED');
  const sentEntry = findLatestStatusEntry(order.statusHistory, 'QUOTE_SENT');
  // Prepared, not sent yet: the order is still a draft.
  const preparing = order.status === 'NEW_INQUIRY';
  const offerSent = order.status === 'QUOTE_SENT';
  // Whoever cannot register the confirmation (action bar) cannot fill it in either.
  const cannotRegister = caps.canChangeStatus === false;
  const [previewStatus, setPreviewStatus] = useState<OfferPdfPreviewStatus>('loading');
  // Any change of the document (lines, prices, customer data, status) reloads the PDF.
  const previewKey = `${order.updatedAt ?? ''}|${(order.products ?? []).map((line) => `${line.id}:${line.quantity}:${line.unitPrice}`).join(',')}`;
  const showPreview = Boolean(loadPdfPreview) && canDownloadPdf;
  // Functional update: two quick changes (tick + date) never overwrite each other.
  const update = (patch: Partial<OfferAcceptance>) => onAcceptanceChange((current) => ({ ...current, ...patch }));
  const sendButton = onSendOffer && !readOnly && (
    <Tooltip title={sendOfferDisabledReason || undefined}>
      <Button size="small" icon={<SendOutlined />} onClick={onSendOffer} disabled={Boolean(sendOfferDisabledReason)} data-testid="zk-offer-send">
        {t('orders:process.offer.send')}
      </Button>
    </Tooltip>
  );

  return (
    <div className="zk-grid zk-grid--offer" data-testid="zk-step-OFFER">
      <StepSection
        title={t('orders:process.offer.previewTitle')}
        aside={(canDownloadPdf || sendButton) && (
          <Space size={8} wrap>
            {canDownloadPdf && (
              <>
                <Segmented<OrderDocumentLanguage>
                  size="small"
                  value={documentLanguage}
                  onChange={(value) => onDocumentLanguageChange(value as OrderDocumentLanguage)}
                  options={[{ label: 'PL', value: 'pl' }, { label: 'EN', value: 'en' }]}
                  aria-label={t('orders:process.offer.language')}
                />
                <Button size="small" icon={<FilePdfOutlined />} loading={pdfDownloading} onClick={onDownloadPdf} data-testid="zk-offer-download">
                  {t('orders:process.offer.download')}
                </Button>
              </>
            )}
            {/* The e-mail is gated on its own (ADMIN, Operacje, Handlowiec), not on the PDF. */}
            {sendButton}
          </Space>
        )}
      >
        {showPreview && loadPdfPreview && (
          <OfferPdfPreview
            load={loadPdfPreview}
            language={documentLanguage}
            refreshKey={previewKey}
            onStatusChange={setPreviewStatus}
          />
        )}
        {/* The table is the preview where the PDF is not shown: no access, a failed load, a phone. */}
        <div className={showPreview && previewStatus !== 'error' ? 'zk-offer-lines zk-offer-lines--narrow-only' : 'zk-offer-lines'} data-testid="zk-offer-lines">
          <OrderLinesSummaryTable order={order} canViewSalePrices={caps.canViewSalePrices} />
        </div>
        {!caps.canViewSalePrices && <Callout tone="muted">{t('orders:process.offer.pricesHidden')}</Callout>}
        <div data-testid="zk-offer-sent-state">
          {preparing ? null : sentEntry ? (
            <Callout tone="success">
              {t('orders:process.offer.sent', {
                date: formatDateTimeEuropean(sentEntry.changedAt),
                author: sentEntry.changedBy?.name || t('orders:orderDetail.historyAuthorUnknown'),
              })}
              {sentEntry.notes ? ` ${sentEntry.notes}` : ''}
            </Callout>
          ) : null}
        </div>
      </StepSection>

      <StepSection title={t('orders:process.offer.acceptanceTitle')} icon={<SafetyCertificateOutlined />} testId="zk-offer-acceptance">
        {readOnly ? (
          confirmedEntry ? (
            <Callout tone="success" flush>
              {t('orders:process.offer.registered', {
                date: formatDateTimeEuropean(confirmedEntry.changedAt),
                author: confirmedEntry.changedBy?.name || t('orders:orderDetail.historyAuthorUnknown'),
              })}
              {confirmedEntry.notes ? ` ${confirmedEntry.notes}` : ''}
            </Callout>
          ) : <Callout tone="muted" flush>{t('orders:process.offer.noRecord')}</Callout>
        ) : (
          <>
            <Callout tone={acceptance.accepted ? 'success' : 'warning'} flush testId="zk-offer-acceptance-state">
              {acceptance.accepted
                ? t('orders:process.offer.acceptedState')
                : offerSent ? t('orders:process.offer.waiting') : t('orders:process.offer.waitingUnsent')}
            </Callout>
            <p className="zk-section__hint zk-offer-hint">{t('orders:process.offer.acceptanceHint')}</p>
            <Form layout="vertical" component="div">
              <Checkbox
                checked={acceptance.accepted}
                onChange={(event) => {
                  const checked = event.target.checked;
                  onAcceptanceChange((current) => ({
                    ...current,
                    accepted: checked,
                    date: checked && !current.date ? dayjs().format('YYYY-MM-DD') : current.date,
                  }));
                }}
                disabled={cannotRegister}
                data-testid="zk-offer-accepted"
              >
                {t('orders:process.offer.acceptedCheckbox')}
              </Checkbox>
              <p className="zk-section__hint zk-offer-checkbox-help">{t('orders:process.offer.acceptedHelp')}</p>
              <div className="zk-offer-fields">
                <Form.Item label={t('orders:process.offer.date')} required>
                  <DatePicker
                    format="DD.MM.YYYY"
                    value={acceptance.date ? dayjs(acceptance.date) : null}
                    onChange={(value) => update({ date: value ? value.format('YYYY-MM-DD') : null })}
                    style={{ width: '100%' }}
                    disabled={cannotRegister}
                    aria-label={t('orders:process.offer.date')}
                  />
                </Form.Item>
                <Form.Item label={t('orders:process.offer.person')}>
                  <Input
                    value={acceptance.person}
                    maxLength={120}
                    placeholder={t('orders:process.offer.personPlaceholder')}
                    disabled={cannotRegister}
                    onChange={(event) => update({ person: event.target.value })}
                  />
                </Form.Item>
              </div>
              <Form.Item label={t('orders:process.offer.notes')}>
                <Input.TextArea
                  rows={3}
                  showCount
                  maxLength={OFFER_ACCEPTANCE_NOTES_MAX}
                  value={acceptance.notes}
                  placeholder={t('orders:process.offer.notesPlaceholder')}
                  disabled={cannotRegister}
                  onChange={(event) => update({ notes: event.target.value })}
                />
              </Form.Item>
            </Form>
          </>
        )}
      </StepSection>
    </div>
  );
}
